import { useState, useRef, useEffect } from 'react';
import {
  X, Grid, FileExclamationPoint, Layout, Palette,
  Save, Download, Undo, Redo, Eye, EyeOff, Trash2,
  Image, Upload, Waves, QrCode, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, GripVertical, ScrollText,
} from 'lucide-react';
import { useTranslation } from '../../../../i18n/i18n';
import { toast } from 'react-toastify';
import { SectionComponents } from './VoucherSections/VoucherComponents';
import { FlightSectionComponents } from './VoucherSections/FlightVoucherSections';
import { HotelSectionComponents } from './VoucherSections/HotelVoucherSections';
import { TransferSectionComponents } from './VoucherSections/TransferVoucherSections';
import {ActivitySectionComponents} from "./VoucherSections/ActivityVoucherSections";
import { VoucherContainer } from './VoucherSections/VoucherComponents';

import { exportToPDF } from './VoucherPdfRenderer';

const VoucherCustomizer = ({
  onClose,
  onSave,
  initialSections = [],
  initialConfig = {},
  lang = 'en',
  getServiceIcon,
  formatDate,
  formatDateTime,
  getStatusColor
}) => {
  const { t } = useTranslation(lang);
  const [sections, setSections] = useState(
    initialSections.map(section => ({
      ...section,
      layoutConfig: section.layoutConfig || {
        layout: 'vertical',
        columns: 1
      }
    }))
  );
  const [config, setConfig] = useState({
    layout: 'vertical',
    columns: 1,
    gap: 32,
    padding: 40,
    fontSize: 16,
    primaryColor: '#7c3aed',       // Couleur primaire (titres)
    secondaryColor: '#6b7280',     // Couleur secondaire (sous-titres)
    textColor: '#1f2937',          // Couleur du texte (écritures)
    backgroundColor: '#ffffff',    // Couleur de fond principale
    sectionColor: '#f8fafc',       // Couleur des sections
    borderRadius: 12,
    showLogo: true,
    logoPosition: 'top-left',
    logoUrl: null,
    elementLayout: 'vertical', // layout pour l'élément sélectionné
    elementColumns: 2, // colonnes pour l'élément sélectionné
    cancellationNotes: {
      enabled: initialConfig?.cancellationNotes?.enabled || false,
      notes: initialConfig?.cancellationNotes?.notes || [],
      customPolicy: initialConfig?.cancellationNotes?.customPolicy || '',
      showSystemInfo: initialConfig?.cancellationNotes?.showSystemInfo || true
    },
    notesConfig: {
      showCustomizationPanel: initialConfig?.notesConfig?.showCustomizationPanel || false,
      notes: initialConfig?.notesConfig?.notes || [
        t('notePresentVoucher') || 'Présentez ce voucher à votre arrivée',
        t('noteKeepVoucher') || 'Conservez ce voucher avec vous',
        t('noteEmergencyContact') || 'Contactez-nous en cas d\'urgence',
        t('noteCancellationPolicies') || 'Consultez nos politiques d\'annulation'
      ],
      showGenerationDate: initialConfig?.notesConfig?.showGenerationDate || true,
      emergencyContact: initialConfig?.notesConfig?.emergencyContact || ''
    },
    ...initialConfig
  });

  const [selectedSection, setSelectedSection] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [history, setHistory] = useState([{ sections: [...initialSections], config: { ...initialConfig } }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const sidebarRef = useRef(null);
  const previewRef = useRef(null);
  const [showControls, setShowControls] = useState(true);

  const sectionsWithCustomLayout = ['bookingInfo', 'holderInfo', 'providerInfo', 'contactInformation', 'servicesInclusionsExclusions', 'cancellationPolicies', 'ActivitySection', 'HotelSection'];

  const FONT_FAMILIES = [
    { value: "'Inter', sans-serif", label: 'Inter (Moderne)', preview: 'Inter' },
    { value: "'Roboto', sans-serif", label: 'Roboto (Clean)', preview: 'Roboto' },
    { value: "'Open Sans', sans-serif", label: 'Open Sans (Lisible)', preview: 'Open Sans' },
    { value: "'Georgia', serif", label: 'Georgia (Classique)', preview: 'Georgia' },
    { value: "'Courier New', monospace", label: 'Courier (Typewriter)', preview: 'Courier New' },
    { value: "'Arial', sans-serif", label: 'Arial (Standard)', preview: 'Arial' }
  ];

  const [logoConfig, setLogoConfig] = useState({
    // Upload et image
    logoFile: null,
    logoUrl: initialConfig?.logoUrl || null,

    // Section Header
    header: {
      enabled: initialConfig?.headerEnabled || false,
      position: initialConfig?.headerPosition || 'left', // 'left', 'center', 'right'
      size: initialConfig?.headerSize || 'medium',
      opacity: initialConfig?.headerOpacity || 100,
      alignment: 'row', // 'row' (côté à côté) ou 'column' (logo au-dessus)
      spacing: 20 // espace entre logo et texte
    },

    // Section Footer
    footer: {
      enabled: initialConfig?.footerEnabled || false,
      position: initialConfig?.footerPosition || 'center',
      size: initialConfig?.footerSize || 'small',
      opacity: initialConfig?.footerOpacity || 100
    },

    // Personnalisation 
    customization: {
      enabled: false,
      x: 40,
      y: 40,
      size: 'large',
      width: 120,
      opacity: 100
    }
  });

  const WATERMARK_POSITIONS = [
    { value: 'center', label: 'Centre', icon: '◎' },
    { value: 'diagonal', label: 'Diagonale', icon: '↗️' },
    { value: 'repeat', label: 'Répétition', icon: '🔁' },
    { value: 'corner', label: 'Coin', icon: '📐' }
  ];

  // Ajoutez WATERMARK_SIZES
  const WATERMARK_SIZES = [
    { value: 'small', label: 'Petit' },
    { value: 'medium', label: 'Moyen' },
    { value: 'large', label: 'Grand' },
    { value: 'xlarge', label: 'Très Grand' }
  ];

  // Mettez à jour l'état initial
  const [watermarkConfig, setWatermarkConfig] = useState({
    showWatermark: initialConfig?.showWatermark || false,
    watermarkText: initialConfig?.watermarkText || 'EXTRANET',
    watermarkType: initialConfig?.watermarkType || 'text',
    watermarkFile: null,
    watermarkUrl: null,
    watermarkOpacity: initialConfig?.watermarkOpacity || 70, 
    watermarkPosition: initialConfig?.watermarkPosition || 'center',
    watermarkRotation: initialConfig?.watermarkRotation || -45,
    watermarkSize: initialConfig?.watermarkSize || 'large' // Taille par défaut
  });

  const [qrCodeConfig, setQrCodeConfig] = useState({
    customQrCode: false,
    qrCodeFile: null,
    qrCodeUrl: null,
    qrCodeSize: 200,
    qrBorderRadius: 8,
    qrMessage: t('scanQRCode'),
    centerLogoUrl: null,
    centerLogoFile: null
  });

  const handleQrCodeUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB max
        toast.error('Fichier trop volumineux (max 2MB)');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setQrCodeConfig(prev => ({
          ...prev,
          qrCodeFile: file,
          qrCodeUrl: e.target.result,
          customQrCode: true
        }));
        toast.success('QR code téléchargé avec succès');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCenterLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) { // 1MB max
        toast.error('Logo trop volumineux (max 1MB)');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setQrCodeConfig(prev => ({
          ...prev,
          centerLogoFile: file,
          centerLogoUrl: e.target.result
        }));
        toast.success('Logo centre téléchargé');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeQrCode = () => {
    setQrCodeConfig(prev => ({
      ...prev,
      qrCodeFile: null,
      qrCodeUrl: null,
      customQrCode: false
    }));
    toast.info('QR code supprimé');
  };

  const removeCenterLogo = () => {
    setQrCodeConfig(prev => ({
      ...prev,
      centerLogoFile: null,
      centerLogoUrl: null
    }));
    toast.info('Logo centre supprimé');
  };

  // Scroll automatique de la sidebar
  useEffect(() => {
    // Desactive le scroll auto 
  }, []);

  // Sauvegarde historique
  const saveToHistory = (newSections, newConfig) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      sections: JSON.parse(JSON.stringify(newSections)),
      config: JSON.parse(JSON.stringify(newConfig))
    });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Gestion du drag & drop améliorée
  const handleDragStart = (e, sectionId) => {
    e.dataTransfer.setData('text/plain', sectionId);
    setDraggingId(sectionId);

    // Ajout d'un effet visuel
    e.dataTransfer.effectAllowed = 'move';

    // Créer un élément de drag visuel
    if (e.target && e.target.style) {
      setTimeout(() => {
        e.target.style.opacity = '0.4';
      }, 0);
    }
  };

  const handleDragOver = (e, sectionId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (sectionId !== dragOverId) {
      setDragOverId(sectionId);
    }
  };

  const handleDragLeave = (e) => {
    // Ne pas supprimer si on entre dans un enfant
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverId(null);
    }
  };

  const handleDragEnd = (e) => {
    // Réinitialiser l'opacité
    if (e.target && e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sectionId = e.dataTransfer.getData('text/plain');
    const currentIndex = sections.findIndex(s => s.id === sectionId);

    if (currentIndex !== -1 && currentIndex !== targetIndex) {
      const newSections = [...sections];
      const [removed] = newSections.splice(currentIndex, 1);
      newSections.splice(targetIndex, 0, removed);

      // Mettre à jour les positions
      const updatedSections = newSections.map((section, idx) => ({
        ...section,
        position: idx
      }));

      setSections(updatedSections);
      saveToHistory(updatedSections, config);

      // Feedback visuel
      toast.success('Section déplacée');
    }

    setDraggingId(null);
    setDragOverId(null);
  };

  // Sélection par clic dans le canvas
  const handleCanvasClick = (e, sectionId) => {
    e.stopPropagation();
    setSelectedSection(sectionId);

    // Charger les paramètres de layout de la section sélectionnée
    const section = sections.find(s => s.id === sectionId);
    if (section?.layoutConfig) {
      setConfig(prev => ({
        ...prev,
        elementLayout: section.layoutConfig.layout,
        elementColumns: section.layoutConfig.columns
      }));
    }
  };

  // Déplacement de section avec flèches
  const moveSection = (sectionId, direction) => {
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < sections.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    const newSections = [...sections];
    [newSections[currentIndex], newSections[newIndex]] = [newSections[newIndex], newSections[currentIndex]];

    // Mettre à jour les positions
    const updatedSections = newSections.map((section, idx) => ({
      ...section,
      position: idx
    }));

    setSections(updatedSections);
    setSelectedSection(sectionId);
    saveToHistory(updatedSections, config);

    toast.success(`Section déplacée ${direction === 'up' ? 'vers le haut' : 'vers le bas'}`);
  };

  // Toggle visibilité
  const toggleVisibility = (sectionId) => {
    const newSections = sections.map(s =>
      s.id === sectionId ? { ...s, visible: !s.visible } : s
    );
    setSections(newSections);
    saveToHistory(newSections, config);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast.error(t('fileTooLarge'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoConfig(prev => ({
          ...prev,
          logoFile: file,
          logoUrl: e.target.result,
          showLogo: true
        }));
        toast.success(t('logoUploaded'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWatermarkUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB max pour filigrane
        toast.error(t('fileTooLarge'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setWatermarkConfig(prev => ({
          ...prev,
          watermarkFile: file,
          watermarkUrl: e.target.result,
          watermarkType: 'image'
        }));
        toast.success(t('watermarkUploaded'));
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour supprimer le logo
  const removeLogo = () => {
    setLogoConfig(prev => ({
      ...prev,
      logoFile: null,
      logoUrl: null,
      showLogo: false
    }));
    toast.info(t('logoRemoved'));
  };

  // Fonction pour supprimer le filigrane
  const removeWatermark = () => {
    setWatermarkConfig(prev => ({
      ...prev,
      watermarkFile: null,
      watermarkUrl: null,
      watermarkText: '',
      showWatermark: false
    }));
    toast.info(t('watermarkRemoved'));
  };

  // Fonction pour la mise en page globale
  const getGlobalLayoutStyle = () => {
    const baseStyle = {
      display: 'grid',
      gap: `${config.gap}px`,
      width: '100%'
    };

    switch (config.layout) {
      case 'horizontal':
        return {
          ...baseStyle,
          gridTemplateColumns: `repeat(${sections.filter(s => s.visible !== false).length}, 1fr)`,
          gridAutoFlow: 'column'
        };
      case 'grid':
        return {
          ...baseStyle,
          gridTemplateColumns: `repeat(${config.columns}, 1fr)`
        };
      case 'vertical':
      default:
        return {
          ...baseStyle,
          gridTemplateColumns: '1fr'
        };
    }
  };

  // Fonction pour le style de mise en page par élément
  const getElementStyle = (section) => {
    const isSelected = selectedSection === section.id;
    const isDragging = draggingId === section.id;
    const isDragOver = dragOverId === section.id;

    const baseStyle = {
      cursor: 'grab',
      position: 'relative',
      opacity: isDragging ? 0.4 : 1,
      border: isDragOver ? '2px dashed #10b981' :
        isSelected ? '2px solid #7c3aed' : '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: isSelected ? '12px' : '8px',
      marginBottom: '12px',
      backgroundColor: isSelected ? 'rgba(124, 58, 237, 0.05)' :
        isDragOver ? 'rgba(16, 185, 129, 0.05)' : '#ffffff',
      transition: 'all 0.2s ease',
      transform: isDragging ? 'scale(0.98)' : 'scale(1)',
      boxShadow: isSelected ? '0 0 0 1px #7c3aed' :
        isDragOver ? '0 0 0 1px #10b981' : '0 1px 3px rgba(0,0,0,0.1)'
    };

    // Récupérer le layout config de la section
    const sectionLayout = section.layoutConfig || { layout: 'vertical', columns: 1 };

    // Si l'élément est sélectionné, utiliser les paramètres temporaires de config
    // Sinon, utiliser les paramètres sauvegardés dans la section
    let layoutProps = {};

    if (isSelected) {
      // En mode édition (section sélectionnée), utiliser les valeurs temporaires
      layoutProps = {
        layout: config.elementLayout || sectionLayout.layout,
        columns: config.elementColumns || sectionLayout.columns
      };
    } else {
      // Sinon, utiliser les valeurs sauvegardées dans la section
      layoutProps = {
        layout: sectionLayout.layout,
        columns: sectionLayout.columns
      };
    }

    return {
      style: baseStyle,
      layoutProps: layoutProps
    };
  };

  const updateSectionLayout = (sectionId, layoutType, columns = 1) => {
    const newSections = sections.map(s =>
      s.id === sectionId
        ? {
          ...s,
          layoutConfig: {
            layout: layoutType,
            columns: columns
          }
        }
        : s
    );

    setSections(newSections);

    // Mettre à jour aussi les valeurs temporaires dans config
    const newConfig = {
      ...config,
      elementLayout: layoutType,
      elementColumns: columns
    };
    setConfig(newConfig);

    saveToHistory(newSections, newConfig);
  };

  // Supprimer section
  const deleteSection = (sectionId) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    const newSections = sections.filter(s => s.id !== sectionId);
    setSections(newSections);
    setSelectedSection(null);
    saveToHistory(newSections, config);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const state = history[newIndex];
      setSections(state.sections);
      setConfig(state.config);

      // Mettre à jour les valeurs temporaires si une section est sélectionnée
      if (selectedSection) {
        const currentSection = state.sections.find(s => s.id === selectedSection);
        if (currentSection?.layoutConfig) {
          setConfig(prev => ({
            ...prev,
            elementLayout: currentSection.layoutConfig.layout,
            elementColumns: currentSection.layoutConfig.columns
          }));
        }
      }
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const state = history[newIndex];
      setSections(state.sections);
      setConfig(state.config);

      // Mettre à jour les valeurs temporaires si une section est sélectionnée
      if (selectedSection) {
        const currentSection = state.sections.find(s => s.id === selectedSection);
        if (currentSection?.layoutConfig) {
          setConfig(prev => ({
            ...prev,
            elementLayout: currentSection.layoutConfig.layout,
            elementColumns: currentSection.layoutConfig.columns
          }));
        }
      }
    }
  };

  // Render une section
  const renderSection = (section) => {
    console.log(section);
    
    let Component = SectionComponents[section.component];
    if (FlightSectionComponents[section.component]) {
      Component = FlightSectionComponents[section.component];
    } else if (HotelSectionComponents[section.component]) {
      Component = HotelSectionComponents[section.component];
    }
     else if (TransferSectionComponents[section.component]) { 
    Component = TransferSectionComponents[section.component];
  }
    else if (ActivitySectionComponents[section.component]) { 
    Component = ActivitySectionComponents[section.component];
  }

    if (!Component) {
      return <div>Composant non trouvé: {section.component}</div>;
    }

    // Récupérer les props de layout pour cette section
    const { layoutProps } = getElementStyle(section);

    const props = {
      data: section,
      config: {
        ...config,
        columns: layoutProps.columns, 
        layout: layoutProps.layout    
      },
      t,
      getServiceIcon,
      formatDate,
      formatDateTime,
      getStatusColor,
      cancellationNotes: section.type === 'cancellation' ? config.cancellationNotes : undefined
    };

    if (section.component === 'HeaderSection') {
      props.logoConfig = logoConfig;
    }

    if (section.component === 'FooterSection') {
      props.logoConfig = logoConfig;
    }

    if (section.component === 'QrCodeSection') {
      props.data = {
        ...section,
        // Configuration personnalisée du QR code
        customQrCodeUrl: qrCodeConfig.qrCodeUrl,
        qrCodeUrl: qrCodeConfig.qrCodeUrl, // Pour compatibilité
        size: qrCodeConfig.qrCodeSize,
        qrBorderRadius: qrCodeConfig.qrBorderRadius,
        showShadow: qrCodeConfig.showShadow,
        message: qrCodeConfig.qrMessage || t('scanQRCode'),
        centerLogoUrl: qrCodeConfig.centerLogoUrl,
        isEditable: true,
        // Autres options personnalisables
        borderStyle: qrCodeConfig.showBorder ? `2px solid ${config.primaryColor}` : undefined,
        background: qrCodeConfig.background || config.backgroundColor,
        textColor: qrCodeConfig.textColor || config.textColor,
        textSize: qrCodeConfig.textSize || 14,
        textWeight: qrCodeConfig.textWeight || 500,
        showEncodedInfo: qrCodeConfig.showEncodedInfo
      };
    }

    // if (section.component === 'ServicesSection') {

    //   if (section.extra_includes && section.extra_includes.length > 0) {
    //         console.log('rrrrr',section);
    // console.log(section.extra_includes.length);
    //   }

    
    // }


    return <Component {...props} />;
  };

  const exportPDF = async () => {
    try {
      toast.info(
        t('generatingPDF'),
        { containerId: "modal"}
      );

      console.log(watermarkConfig);
      
      await exportToPDF({
        sections: sections.filter(s => s.visible !== false),
        renderSection: renderSection,
        config: config,
        watermarkConfig: watermarkConfig,
        logoConfig: logoConfig
      });

      toast.success(t('pdfGenerated'), { containerId: "modal" });
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(t('pdfError') || 'Erreur lors de la génération du PDF', { containerId: "modal" });
    }
  };

  // Sauvegarder configuration
  const saveConfiguration = () => {
    const configuration = {
      sections: sections.map((s, idx) => ({
        id: s.id,
        type: s.type,
        component: s.component,
        data: s,
        position: idx,
        visible: s.visible !== false
      })),
      config,
      metadata: {
        version: '1.0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    onSave(configuration);
    toast.success(t('configurationSaved'));
  };

  // Styles unifiés
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000
    },
    header: {
      padding: '16px 24px',
      backgroundColor: '#111827',
      borderBottom: '1px solid #374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    title: {
      color: '#fff',
      fontSize: '18px',
      fontWeight: 'bold',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    content: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden'
    },
    leftPanel: {
      width: '320px',
      backgroundColor: '#1f2937',
      borderRight: '1px solid #374151',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      flexShrink: 0
    },
    leftPanelContent: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    toggleButton: {
      position: 'absolute',
      top: '50%',
      right: showControls ? '320px' : '0',
      transform: 'translateY(-50%)',
      backgroundColor: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 8px',
      cursor: 'pointer',
      zIndex: 1000,
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
    },
    controlsPanel: {
      width: showControls ? '320px' : '0',
      backgroundColor: '#1f2937',
      borderLeft: '1px solid #374151',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      flexShrink: 0
    },
    controlsContent: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      opacity: showControls ? 1 : 0,
      transition: 'opacity 0.3s ease'
    },
    mainArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: '#374151'
    },
    previewArea: {
      flex: 1,
      padding: '2px',
      overflow: 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    },
    voucherPreview: {
      backgroundColor: config.backgroundColor,
      borderRadius: `${config.borderRadius}px`,
      padding: `${config.padding}px`,
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: `${config.fontSize}px`,
      transition: 'all 0.3s ease'
    },
    controls: {
      padding: '16px',
      backgroundColor: '#111827',
      borderTop: '1px solid #374151',
      flexShrink: 0
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 500,
      fontSize: '14px',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: '#6d28d9'
      }
    },
    configSection: {
      backgroundColor: '#2d3748',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #4a5568',
      transition: 'all 0.3s ease'
    },
    configTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#f6f7faff',
      margin: '0 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      paddingBottom: '12px',
      borderBottom: '1px solid #4a5568'
    },
    configGroup: {
      marginBottom: '16px'
    },
    switchLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      userSelect: 'none',
      padding: '8px 0'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
      accentColor: '#7c3aed'
    },
    switchText: {
      fontSize: '14px',
      color: '#ffffffff',
      fontWeight: '500',
      marginRight: '8px'
    },
    fileUploadLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      background: '#374151',
      border: '2px dashed #6b7280',
      borderRadius: '8px',
      cursor: 'pointer',
      color: '#d1d5db',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      width: '100%',
      '&:hover': {
        borderColor: '#7c3aed',
        backgroundColor: '#4b5563'
      }
    },
    fileInput: {
      display: 'none'
    },
    removeButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      marginTop: '8px',
      background: '#ef4444',
      border: 'none',
      borderRadius: '8px',
      color: 'white',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: '100%',
      '&:hover': {
        background: '#dc2626'
      }
    },
    positionGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '10px',
      marginTop: '8px'
    },
    positionButton: {
      width: '44px',
      height: '44px',
      border: '2px solid #4a5568',
      borderRadius: '8px',
      fontSize: '18px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#374151',
      color: '#d1d5db',
      '&:hover': {
        borderColor: '#7c3aed'
      }
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#4a5568',
      outline: 'none',
      opacity: 0.7,
      transition: 'opacity 0.2s',
      WebkitAppearance: 'none',
      appearance: 'none',
      marginTop: '8px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      marginTop: '8px'
    },
    tabButton: {
      flex: 1,
      padding: '12px 16px',
      border: '2px solid #4a5568',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: '#374151',
      color: '#d1d5db',
      '&:hover': {
        borderColor: '#7c3aed'
      }
    },
    previewContainer: {
      marginTop: '16px',
      padding: '16px',
      background: '#374151',
      border: '1px solid #4a5568',
      borderRadius: '8px'
    },
    previewTitle: {
      fontSize: '13px',
      color: '#9ca3af',
      marginBottom: '12px',
      fontWeight: '500'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '2px solid #4a5568',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#d1d5db',
      background: '#374151',
      cursor: 'pointer',
      outline: 'none',
      transition: 'border 0.2s ease',
      '&:focus': {
        borderColor: '#7c3aed'
      }
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '2px solid #4a5568',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#d1d5db',
      background: '#374151',
      outline: 'none',
      transition: 'border 0.2s ease',
      '&:focus': {
        borderColor: '#7c3aed'
      }
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '2px solid #4a5568',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#d1d5db',
      background: '#374151',
      outline: 'none',
      transition: 'border 0.2s ease',
      minHeight: '80px',
      resize: 'vertical',
      '&:focus': {
        borderColor: '#7c3aed'
      }
    },
    sectionItem: {
      padding: '14px',
      backgroundColor: '#2d3748',
      borderRadius: '10px',
      color: '#fff',
      cursor: 'grab',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '10px',
      transition: 'all 0.2s ease',
      userSelect: 'none',
      '&:hover': {
        borderColor: '#7c3aed',
        backgroundColor: '#374151'
      }
    },
    sectionItemSelected: {
      borderColor: '#7c3aed',
      backgroundColor: 'rgba(124, 58, 237, 0.15)',
      boxShadow: '0 0 0 1px #7c3aed'
    },
    sectionItemDragging: {
      opacity: 0.5,
      borderStyle: 'dashed',
      transform: 'scale(0.98)'
    },
    sectionItemDragOver: {
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)'
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0
    },
    nestedConfigGroup: {
      padding: '10px',
      border: '1px solid #4a5568',
      borderRadius: '6px',
      marginTop: '10px'
    },
    nestedTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#d1d5db',
      marginBottom: '8px',
      borderBottom: '1px solid #4a5568',
      paddingBottom: '5px'
    },
    configSubGroup: {
      marginBottom: '15px',
    },
    label: {
      display: 'block',
      color: '#9ca3af',
      fontSize: '12px',
      marginBottom: '5px',
    },
  };

  return (
    <div style={styles.overlay}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>
          <Palette size={20} />
          {t('voucherCustomizer') || 'Personnalisation du Voucher'}
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#374151'}
          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      <div style={styles.content}>
        {/* Panel gauche - Sections */}
        <div style={styles.leftPanel}>
          <div style={styles.leftPanelContent}>
            <div>
              <h3 style={{
                color: '#fff',
                margin: '0 0 16px 0',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>{t('sections')} ({sections.length})</span>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {t('dragToReorder')}
                </span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sections.map((section, index) => (
                  <div
                    key={section.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, section.id)}
                    onDragOver={(e) => handleDragOver(e, section.id)}
                    onDragLeave={handleDragLeave}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, index)}
                    style={{
                      ...styles.sectionItem,
                      ...(selectedSection === section.id ? styles.sectionItemSelected : {}),
                      ...(draggingId === section.id ? styles.sectionItemDragging : {}),
                      ...(dragOverId === section.id ? styles.sectionItemDragOver : {})
                    }}
                    onClick={() => setSelectedSection(section.id)}
                  >
                    <GripVertical size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      minWidth: 0
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={styles.sectionTitle}>
                          {section.visible !== false ? '●' : '○'} {section.name}
                        </span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveSection(section.id, 'up');
                            }}
                            disabled={index === 0}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: index === 0 ? '#4a5568' : '#9ca3af',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: index === 0 ? 0.5 : 1
                            }}
                            title="Monter"
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveSection(section.id, 'down');
                            }}
                            disabled={index === sections.length - 1}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: index === sections.length - 1 ? '#4a5568' : '#9ca3af',
                              cursor: index === sections.length - 1 ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: index === sections.length - 1 ? 0.5 : 1
                            }}
                            title="Descendre"
                          >
                            <ChevronDown size={16} />
                          </button>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        fontSize: '12px',
                        color: '#9ca3af'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleVisibility(section.id);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: section.visible !== false ? '#10b981' : '#9ca3af',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {section.visible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                          {section.visible !== false ? 'Visible' : 'Caché'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSection(section.id);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Trash2 size={14} /> Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bouton toggle pour contrôles */}
        <button
          onClick={() => setShowControls(!showControls)}
          style={styles.toggleButton}
          title={showControls ? 'Masquer les contrôles' : 'Afficher les contrôles'}
        >
          {showControls ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>

        {/* Zone principale - Prévisualisation */}
        <div style={styles.mainArea}>
          <div ref={previewRef} style={styles.previewArea}>
            <div
              style={{
                ...styles.voucherPreview,
                ...getGlobalLayoutStyle()
              }}
              data-voucher-preview
              onDragOver={(e) => {
                e.preventDefault();
                if (!dragOverId) setDragOverId('canvas');
              }}
              onDragLeave={() => setDragOverId(null)}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setSelectedSection(null);
                }
              }}
            >
              <VoucherContainer
                config={config}
                logoConfig={logoConfig}
                setLogoConfig={setLogoConfig}
                watermarkConfig={watermarkConfig}
                t={t}
              >

                {sections
                  .filter(section => section.visible !== false)
                  .map((section, index) => {
                    const { style, layoutProps } = getElementStyle(section);
                    const isSelected = selectedSection === section.id;

                    return (
                      <div
                        key={section.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, section.id)}
                        onDragOver={(e) => handleDragOver(e, section.id)}
                        onDragLeave={handleDragLeave}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{...style, backgroundColor: config.sectionColor}}
                        onClick={(e) => handleCanvasClick(e, section.id)}
                      >
                        {renderSection(section)}

                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: -2,
                            left: -2,
                            right: -2,
                            bottom: -2,
                            border: '2px solid #7c3aed',
                            borderRadius: '10px',
                            pointerEvents: 'none',
                            animation: 'pulse 2s infinite'
                          }} />
                        )}
                      </div>
                    );
                  })}

                {sections.filter(s => s.visible !== false).length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#9ca3af',
                    padding: '40px',
                    fontSize: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '2px dashed #e5e7eb'
                  }}>
                    {t('noSectionsAdded')}
                  </div>
                )}
              </VoucherContainer>
            </div>
          </div>

          {/* Barre de contrôle inférieure */}
          <div style={styles.controls}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={undo}
                  disabled={historyIndex === 0}
                  style={{
                    ...styles.button,
                    backgroundColor: historyIndex === 0 ? '#4a5568' : '#7c3aed',
                    opacity: historyIndex === 0 ? 0.5 : 1,
                    cursor: historyIndex === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title="Annuler"
                >
                  <Undo size={16} /> {t('undo') || 'Annuler'}
                </button>
                <button
                  onClick={redo}
                  disabled={historyIndex === history.length - 1}
                  style={{
                    ...styles.button,
                    backgroundColor: historyIndex === history.length - 1 ? '#4a5568' : '#7c3aed',
                    opacity: historyIndex === history.length - 1 ? 0.5 : 1,
                    cursor: historyIndex === history.length - 1 ? 'not-allowed' : 'pointer'
                  }}
                  title="Refaire"
                >
                  <Redo size={16} /> {t('redo') || 'Refaire'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={exportPDF}
                  style={styles.button}
                  title="Exporter en PDF"
                >
                  <Download size={16} /> {t('exportPDF') || 'Exporter PDF'}
                </button>
                <button
                  onClick={saveConfiguration}
                  style={{
                    ...styles.button,
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669'
                    }
                  }}
                  title="Sauvegarder la configuration"
                >
                  <Save size={16} /> {t('saveConfiguration') || 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Panel droit - Contrôles */}
        <div style={styles.controlsPanel}>
          <div style={styles.controlsContent}>

            {/* Section Apparence avec les 4 couleurs */}
            <div style={styles.configSection}>
              <h3 style={styles.configTitle}>
                <Palette size={20} /> {t('appearanceTitle') || 'Apparence - Couleurs'}
              </h3>

              {/* Couleur de fond principale */}
              <div style={styles.configGroup}>
                <label style={styles.label}>{t('backgroundColorLabel') || 'Couleur de fond principale'}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={config.backgroundColor}
                    onChange={(e) => {
                      const newConfig = { ...config, backgroundColor: e.target.value };
                      setConfig(newConfig);
                    }}
                    onBlur={() => saveToHistory(sections, config)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '2px solid #4a5568',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: '#d1d5db',
                    backgroundColor: '#374151',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {config.backgroundColor}
                  </span>
                </div>
              </div>

              {/* Couleur des sections */}
              <div style={styles.configGroup}>
                <label style={styles.label}>{t('sectionColorLabel') || 'Couleur des sections'}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={config.sectionColor}
                    onChange={(e) => {
                      const newConfig = { ...config, sectionColor: e.target.value };
                      setConfig(newConfig);
                    }}
                    onBlur={() => saveToHistory(sections, config)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '2px solid #4a5568',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: '#d1d5db',
                    backgroundColor: '#374151',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {config.sectionColor}
                  </span>
                </div>
              </div>

              {/* Couleur primaire (titres) */}
              <div style={styles.configGroup}>
                <label style={styles.label}>{t('primaryColorLabel') || 'Couleur primaire (titres)'}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => {
                      const newConfig = { ...config, primaryColor: e.target.value };
                      setConfig(newConfig);
                    }}
                    onBlur={() => saveToHistory(sections, config)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '2px solid #4a5568',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: '#d1d5db',
                    backgroundColor: '#374151',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {config.primaryColor}
                  </span>
                </div>
              </div>

              {/* Couleur du texte (écritures) */}
              <div style={styles.configGroup}>
                <label style={styles.label}>{t('textColorLabel') || 'Couleur du texte (écritures)'}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={config.textColor}
                    onChange={(e) => {
                      const newConfig = { ...config, textColor: e.target.value };
                      setConfig(newConfig);
                    }}
                    onBlur={() => saveToHistory(sections, config)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '2px solid #4a5568',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: '#d1d5db',
                    backgroundColor: '#374151',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {config.textColor}
                  </span>
                </div>
              </div>

              {/* Couleur secondaire (sous-titres) */}
              <div style={styles.configGroup}>
                <label style={styles.label}>{t('secondaryColorLabel') || 'Couleur secondaire (sous-titres)'}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={config.secondaryColor}
                    onChange={(e) => {
                      const newConfig = { ...config, secondaryColor: e.target.value };
                      setConfig(newConfig);
                    }}
                    onBlur={() => saveToHistory(sections, config)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '2px solid #4a5568',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: '#d1d5db',
                    backgroundColor: '#374151',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {config.secondaryColor}
                  </span>
                </div>
              </div>

              <div style={styles.configGroup}>
                <label style={styles.label}>
                  {t('fontSizeLabel') || 'Taille de police'}: {config.fontSize}px
                </label>
                <input
                  type="range"
                  min="12"
                  max="24"
                  value={config.fontSize}
                  onChange={(e) => {
                    const newConfig = { ...config, fontSize: parseInt(e.target.value) };
                    setConfig(newConfig);
                  }}
                  onMouseUp={() => saveToHistory(sections, config)}
                  style={styles.slider}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '4px'
                }}>
                  <span>{t('fontSizeSmall') || 'Petit'}</span>
                  <span>{t('fontSizeLarge') || 'Grand'}</span>
                </div>
              </div>

              <div style={styles.configGroup}>
                <label style={styles.label}>
                  {t('spacingLabel') || 'Espacement'}: {config.gap}px
                </label>
                <input
                  type="range"
                  min="10"
                  max="60"
                  value={config.gap}
                  onChange={(e) => {
                    const newConfig = { ...config, gap: parseInt(e.target.value) };
                    setConfig(newConfig);
                  }}
                  onMouseUp={() => saveToHistory(sections, config)}
                  style={styles.slider}
                />
              </div>

              <div style={styles.configGroup}>
                <label style={styles.label}>
                  {t('padding') || 'Padding'}: {config.padding}px
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={config.padding}
                  onChange={(e) => {
                    const newConfig = { ...config, padding: parseInt(e.target.value) };
                    setConfig(newConfig);
                  }}
                  onMouseUp={() => saveToHistory(sections, config)}
                  style={styles.slider}
                />
              </div>

              <div style={styles.configGroup}>
                <label style={styles.label}>
                  {t('borderRadiusLabel') || 'Bord arrondi'}: {config.borderRadius}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="24"
                  value={config.borderRadius}
                  onChange={(e) => {
                    const newConfig = { ...config, borderRadius: parseInt(e.target.value) };
                    setConfig(newConfig);
                  }}
                  onMouseUp={() => saveToHistory(sections, config)}
                  style={styles.slider}
                />
              </div>

              <div style={styles.configGroup}>
                <label style={styles.label}>{t('fontFamilyLabel') || 'Police de caractères'}</label>
                <select
                  value={config.fontFamily || "'Inter', sans-serif"}
                  onChange={(e) => {
                    const newConfig = { ...config, fontFamily: e.target.value };
                    setConfig(newConfig);
                    saveToHistory(sections, newConfig);
                  }}
                  style={styles.select}
                >
                  {FONT_FAMILIES.map(font => (
                    <option key={font.value} value={font.value}>
                      {font.label}
                    </option>
                  ))}
                </select>
                <div style={{
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginTop: '4px',
                  fontFamily: config.fontFamily || "'Inter', sans-serif"
                }}>
                  {t('fontPreview') || 'Aperçu'}: {FONT_FAMILIES.find(f => f.value === config.fontFamily)?.preview || 'Inter'}
                </div>
              </div>

            </div>

            <div style={styles.configSection}>
              <h3 style={styles.configTitle}>
                <Image size={20} /> {t('logoConfiguration') || 'Configuration du Logo'}
              </h3>

              <div style={styles.configGroup}>
                <label style={styles.label}>{t('uploadLogo') || 'Télécharger un logo'}</label>
                <label style={styles.fileUploadLabel}>
                  <Upload size={16} />
                  {t('chooseFile') || 'Choisir un fichier'}
                  <input
                    type="file"
                    accept="image/*,.svg,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={handleLogoUpload}
                    style={styles.fileInput}
                  />
                </label>
                {logoConfig.logoUrl && (
                  <>
                    <button
                      onClick={() => setLogoConfig(prev => ({ ...prev, logoUrl: null, logoFile: null }))}
                      style={styles.removeButton}
                    >
                      <Trash2 size={14} /> {t('removeLogo') || 'Supprimer'}
                    </button>
                    <div style={styles.previewContainer}>
                      <div style={styles.previewTitle}>{t('logoPreview') || 'Aperçu'}:</div>
                      <img
                        src={logoConfig.logoUrl}
                        alt="Logo"
                        draggable={false}
                        style={{
                          width: 200,
                          height: 'auto',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* 2. Options Header */}
              <div style={styles.configGroup}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={styles.switchLabel}>
                    <input
                      type="checkbox"
                      checked={logoConfig.header.enabled}
                      onChange={(e) => setLogoConfig(prev => ({
                        ...prev,
                        header: { ...prev.header, enabled: e.target.checked }
                      }))}
                      style={styles.checkbox}
                    />
                    <span style={styles.switchText}>{t('headerLogo') || 'Afficher le Logo dans l\'en-tête'}</span>
                  </label>
                </div>

                {logoConfig.header.enabled && (
                  <div style={styles.nestedConfigGroup}>
                    <h4 style={styles.nestedTitle}>{t('headerLogoSettings') || 'Paramètres de l\'en-tête'}</h4>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>

                      {/* Sélecteur de Position */}
                      <div style={{ flex: 1 }}>
                        <label style={styles.label}>{t('positionLabel') || 'Position'}</label>
                        <select
                          value={logoConfig.header.position}
                          onChange={(e) => setLogoConfig(prev => ({
                            ...prev,
                            header: { ...prev.header, position: e.target.value }
                          }))}
                          style={styles.select}
                        >
                          <option value="left">{t('left') || 'Gauche'}</option>
                          <option value="center">{t('center') || 'Centre'}</option>
                          <option value="right">{t('right') || 'Droite'}</option>
                        </select>
                      </div>

                      {/* Sélecteur de Taille */}
                      <div style={{ flex: 1 }}>
                        <label style={styles.label}>{t('sizeLabel') || 'Taille'}</label>
                        <select
                          value={logoConfig.header.size}
                          onChange={(e) => setLogoConfig(prev => ({
                            ...prev,
                            header: { ...prev.header, size: e.target.value }
                          }))}
                          style={styles.select}
                        >
                          <option value="small">{t('small') || 'Petite'}</option>
                          <option value="medium">{t('medium') || 'Moyenne'}</option>
                          <option value="large">{t('large') || 'Grande'}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Options Footer */}
              <div style={styles.configGroup}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={styles.switchLabel}>
                    <input
                      type="checkbox"
                      checked={logoConfig.footer.enabled}
                      onChange={(e) => setLogoConfig(prev => ({
                        ...prev,
                        footer: { ...prev.footer, enabled: e.target.checked }
                      }))}
                      style={styles.checkbox}
                    />
                    <span style={styles.switchText}>{t('footerLogo') || 'Afficher le Logo dans le pied de page'}</span>
                  </label>
                </div>

                {logoConfig.footer.enabled && (
                  <div style={styles.nestedConfigGroup}>
                    <h4 style={styles.nestedTitle}>{t('footerLogoSettings') || 'Paramètres du pied de page'}</h4>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>

                      {/* Sélecteur de Position */}
                      <div style={{ flex: 1 }}>
                        <label style={styles.label}>{t('positionLabel') || 'Position'}</label>
                        <select
                          value={logoConfig.footer.position}
                          onChange={(e) => setLogoConfig(prev => ({
                            ...prev,
                            footer: { ...prev.footer, position: e.target.value }
                          }))}
                          style={styles.select}
                        >
                          <option value="left">{t('left') || 'Gauche'}</option>
                          <option value="center">{t('center') || 'Centre'}</option>
                          <option value="right">{t('right') || 'Droite'}</option>
                        </select>
                      </div>

                      {/* Sélecteur de Taille */}
                      <div style={{ flex: 1 }}>
                        <label style={styles.label}>{t('sizeLabel') || 'Taille'}</label>
                        <select
                          value={logoConfig.footer.size}
                          onChange={(e) => setLogoConfig(prev => ({
                            ...prev,
                            footer: { ...prev.footer, size: e.target.value }
                          }))}
                          style={styles.select}
                        >
                          <option value="small">{t('small') || 'Petite'}</option>
                          <option value="medium">{t('medium') || 'Moyenne'}</option>
                          <option value="large">{t('large') || 'Grande'}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 4. Options Custom (Filigrane) */}
              <div style={styles.configGroup}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={styles.switchLabel}>
                    <input
                      type="checkbox"
                      checked={logoConfig.customization.enabled}
                      onChange={(e) => setLogoConfig(prev => ({
                        ...prev,
                        customization: { ...prev.customization, enabled: e.target.checked }
                      }))}
                      style={styles.checkbox}
                    />
                    <span style={styles.switchText}>{t('customLogo') || 'Afficher le Logo en filigrane'}</span>
                  </label>
                </div>

                {logoConfig.customization.enabled && (
                  <div style={styles.nestedConfigGroup}>
                    <h4 style={styles.nestedTitle}>
                      {t('customLogoSettings') || 'Position du logo'}
                    </h4>

                    {/* X */}
                    <div style={styles.configSubGroup}>
                      <label style={styles.label}>
                        X : {logoConfig.customization.x}px
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="700"
                        value={logoConfig.customization.x}
                        onChange={(e) =>
                          setLogoConfig(prev => ({
                            ...prev,
                            customization: {
                              ...prev.customization,
                              x: Number(e.target.value)
                            }
                          }))
                        }
                        style={styles.slider}
                      />
                    </div>

                    {/* Y */}
                    <div style={styles.configSubGroup}>
                      <label style={styles.label}>
                        Y : {logoConfig.customization.y}px
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        value={logoConfig.customization.y}
                        onChange={(e) =>
                          setLogoConfig(prev => ({
                            ...prev,
                            customization: {
                              ...prev.customization,
                              y: Number(e.target.value)
                            }
                          }))
                        }
                        style={styles.slider}
                      />
                    </div>

                    {/* TAILLE DU LOGO */}
                    <div style={styles.configSubGroup}>
                      <label style={styles.label}>
                        Taille du logo : {logoConfig.customization.width}px
                      </label>
                      <input
                        type="range"
                        min="40"
                        max="300"
                        value={logoConfig.customization.width}
                        onChange={(e) =>
                          setLogoConfig(prev => ({
                            ...prev,
                            customization: {
                              ...prev.customization,
                              width: Number(e.target.value)
                            }
                          }))
                        }
                        style={styles.slider}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section Filigrane */}
            <div style={styles.configSection}>
              <h3 style={styles.configTitle}>
                <Waves size={20} /> {t('watermarkConfiguration') || 'Configuration du Filigrane'}
              </h3>

              <div style={styles.configGroup}>
                <label style={styles.switchLabel}>
                  <input
                    type="checkbox"
                    checked={watermarkConfig.showWatermark}
                    onChange={(e) => setWatermarkConfig(prev => ({
                      ...prev,
                      showWatermark: e.target.checked
                    }))}
                    style={styles.checkbox}
                  />
                  <span style={styles.switchText}>{t('showWatermark') || 'Afficher le filigrane'}</span>
                </label>
              </div>

              {watermarkConfig.showWatermark && (
                <>
                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('watermarkType') || 'Type de filigrane'}</label>
                    <div style={styles.buttonGroup}>
                      <button
                        onClick={() => setWatermarkConfig(prev => ({
                          ...prev,
                          watermarkType: 'text'
                        }))}
                        style={{
                          ...styles.tabButton,
                          background: watermarkConfig.watermarkType === 'text'
                            ? '#7c3aed'
                            : '#374151',
                          color: watermarkConfig.watermarkType === 'text'
                            ? 'white'
                            : '#d1d5db',
                          borderColor: watermarkConfig.watermarkType === 'text'
                            ? '#7c3aed'
                            : '#4a5568'
                        }}
                      >
                        {t('textWatermark') || 'Texte'}
                      </button>
                      <button
                        onClick={() => setWatermarkConfig(prev => ({
                          ...prev,
                          watermarkType: 'image'
                        }))}
                        style={{
                          ...styles.tabButton,
                          background: watermarkConfig.watermarkType === 'image'
                            ? '#7c3aed'
                            : '#374151',
                          color: watermarkConfig.watermarkType === 'image'
                            ? 'white'
                            : '#d1d5db',
                          borderColor: watermarkConfig.watermarkType === 'image'
                            ? '#7c3aed'
                            : '#4a5568'
                        }}
                      >
                        {t('imageWatermark') || 'Image'}
                      </button>
                    </div>
                  </div>

                  {watermarkConfig.watermarkType === 'text' ? (
                    <div style={styles.configGroup}>
                      <label style={styles.label}>{t('watermarkText') || 'Texte du filigrane'}</label>
                      <input
                        type="text"
                        value={watermarkConfig.watermarkText}
                        onChange={(e) => setWatermarkConfig(prev => ({
                          ...prev,
                          watermarkText: e.target.value
                        }))}
                        placeholder={t('enterWatermarkText') || 'Entrez le texte'}
                        style={styles.input}
                      />
                    </div>
                  ) : (
                    <div style={styles.configGroup}>
                      <label style={styles.fileUploadLabel}>
                        <Upload size={16} />
                        {t('uploadWatermarkImage') || 'Télécharger une image'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleWatermarkUpload}
                          style={styles.fileInput}
                        />
                      </label>
                      {watermarkConfig.watermarkUrl && (
                        <button
                          onClick={removeWatermark}
                          style={styles.removeButton}
                        >
                          <Trash2 size={14} /> {t('removeWatermark') || 'Supprimer le filigrane'}
                        </button>
                      )}
                    </div>
                  )}

                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('watermarkSize') || 'Taille du filigrane'}</label>
                    <select
                      value={watermarkConfig.watermarkSize}
                      onChange={(e) => setWatermarkConfig(prev => ({
                        ...prev,
                        watermarkSize: e.target.value
                      }))}
                      style={styles.select}
                    >
                      {WATERMARK_SIZES.map(size => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('watermarkPosition') || 'Position du filigrane'}</label>
                    <select
                      value={watermarkConfig.watermarkPosition}
                      onChange={(e) => setWatermarkConfig(prev => ({
                        ...prev,
                        watermarkPosition: e.target.value
                      }))}
                      style={styles.select}
                    >
                      {WATERMARK_POSITIONS.map(pos => (
                        <option key={pos.value} value={pos.value}>
                          {pos.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.configGroup}>
                    <label style={styles.label}>
                      {t('watermarkOpacity') || 'Opacité du filigrane'}: {watermarkConfig.watermarkOpacity}%
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={watermarkConfig.watermarkOpacity}
                      onChange={(e) => setWatermarkConfig(prev => ({
                        ...prev,
                        watermarkOpacity: parseInt(e.target.value)
                      }))}
                      style={styles.slider}
                    />
                  </div>

                  <div style={styles.configGroup}>
                    <label style={styles.label}>
                      {t('watermarkRotation') || 'Rotation du filigrane'}: {watermarkConfig.watermarkRotation}°
                    </label>
                    <input
                      type="range"
                      min="-90"
                      max="90"
                      value={watermarkConfig.watermarkRotation}
                      onChange={(e) => setWatermarkConfig(prev => ({
                        ...prev,
                        watermarkRotation: parseInt(e.target.value)
                      }))}
                      style={styles.slider}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Section QR Code Personnalisé */}
            <div style={styles.configSection}>
              <h3 style={styles.configTitle}>
                <QrCode size={20} /> {t('customQrCode') || 'QR Code Personnalisé'}
              </h3>

              <div style={styles.configGroup}>
                <label style={styles.switchLabel}>
                  <input
                    type="checkbox"
                    checked={qrCodeConfig.customQrCode}
                    onChange={(e) => setQrCodeConfig(prev => ({
                      ...prev,
                      customQrCode: e.target.checked
                    }))}
                    style={styles.checkbox}
                  />
                  <span style={styles.switchText}>{t('useCustomQrCode') || 'Utiliser un QR code personnalisé'}</span>
                </label>
              </div>

              {qrCodeConfig.customQrCode && (
                <>
                  <div style={styles.configGroup}>
                    <label style={styles.fileUploadLabel}>
                      <Upload size={16} />
                      {t('uploadQrCodeImage') || 'Télécharger un QR code'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleQrCodeUpload}
                        style={styles.fileInput}
                      />
                    </label>
                    {qrCodeConfig.qrCodeUrl && (
                      <button
                        onClick={removeQrCode}
                        style={styles.removeButton}
                      >
                        <Trash2 size={14} /> {t('removeQrCode') || 'Supprimer le QR code'}
                      </button>
                    )}
                  </div>

                  {qrCodeConfig.qrCodeUrl && (
                    <div style={styles.previewContainer}>
                      <div style={styles.previewTitle}>{t('qrCodePreview') || 'Aperçu du QR code'}:</div>
                      <img
                        src={qrCodeConfig.qrCodeUrl}
                        alt="QR Code"
                        style={{
                          width: 150,
                          height: 150,
                          border: '2px solid #4a5568',
                          borderRadius: '8px',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}

                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('qrCodeSize') || 'Taille du QR code'}</label>
                    <input
                      type="range"
                      min="100"
                      max="500"
                      step="10"
                      value={qrCodeConfig.qrCodeSize}
                      onChange={(e) => setQrCodeConfig(prev => ({
                        ...prev,
                        qrCodeSize: parseInt(e.target.value)
                      }))}
                      style={styles.slider}
                    />
                    <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'right', marginTop: '4px' }}>
                      {qrCodeConfig.qrCodeSize}px
                    </div>
                  </div>

                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('qrCodeMessage') || 'Message sous le QR code'}</label>
                    <textarea
                      value={qrCodeConfig.qrMessage}
                      onChange={(e) => setQrCodeConfig(prev => ({
                        ...prev,
                        qrMessage: e.target.value
                      }))}
                      placeholder={t('enterQrMessage') || 'Entrez un message...'}
                      style={styles.textarea}
                    />
                  </div>

                  <div style={styles.configGroup}>
                    <label style={styles.fileUploadLabel}>
                      <Image size={16} />
                      {t('uploadCenterLogo') || 'Logo au centre du QR code'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCenterLogoUpload}
                        style={styles.fileInput}
                      />
                    </label>
                    {qrCodeConfig.centerLogoUrl && (
                      <button
                        onClick={removeCenterLogo}
                        style={styles.removeButton}
                      >
                        <Trash2 size={14} /> {t('removeLogo') || 'Supprimer le logo'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Mise en page par élément */}
            {selectedSection && sectionsWithCustomLayout.includes(selectedSection) && (() => {
              const selectedSectionData = sections.find(s => s.id === selectedSection);
              const sectionLayout = selectedSectionData?.layoutConfig || { layout: 'vertical', columns: 1 };

              return (
                <div style={styles.configSection}>
                  <h3 style={styles.configTitle}>
                    <Layout size={20} /> {t('layoutConfigTitle') || 'Mise en page'}: {selectedSectionData?.name}
                  </h3>

                  <div style={styles.configGroup}>
                    <label style={styles.label}>{t('layoutTypeLabel') || 'Type de mise en page'}</label>
                    <div style={styles.buttonGroup}>
                      {[
                        { id: 'vertical', icon: <Layout size={16} />, labelKey: 'layoutVertical', defaultLabel: 'Vertical' },
                        { id: 'grid', icon: <Grid size={16} />, labelKey: 'layoutGrid', defaultLabel: 'Grille' }
                      ].map((layout) => (
                        <button
                          key={layout.id}
                          onClick={() => {
                            updateSectionLayout(selectedSection, layout.id, layout.id === 'grid' ? 2 : 1);
                          }}
                          style={{
                            ...styles.tabButton,
                            background: (config.elementLayout || sectionLayout.layout) === layout.id
                              ? '#7c3aed'
                              : '#374151',
                            color: (config.elementLayout || sectionLayout.layout) === layout.id
                              ? 'white'
                              : '#d1d5db',
                            borderColor: (config.elementLayout || sectionLayout.layout) === layout.id
                              ? '#7c3aed'
                              : '#4a5568'
                          }}
                        >
                          {layout.icon} {t(layout.labelKey) || layout.defaultLabel}
                        </button>
                      ))}
                    </div>
                  </div>

                  {config.elementLayout === 'grid' && (
                    <div style={styles.configGroup}>
                      <label style={styles.label}>{t('columnCountLabel') || 'Nombre de colonnes'}</label>
                      <div style={styles.buttonGroup}>
                        {[1, 2, 3, 4].map(num => (
                          <button
                            key={num}
                            onClick={() => {
                              updateSectionLayout(selectedSection, config.elementLayout || sectionLayout.layout, num);
                            }}
                            style={{
                              ...styles.tabButton,
                              background: (config.elementColumns || sectionLayout.columns) === num
                                ? '#7c3aed'
                                : '#374151',
                              color: (config.elementColumns || sectionLayout.columns) === num
                                ? 'white'
                                : '#d1d5db',
                              borderColor: (config.elementColumns || sectionLayout.columns) === num
                                ? '#7c3aed'
                                : '#4a5568'
                            }}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={styles.configGroup}>
                    <button
                      onClick={() => {
                        updateSectionLayout(selectedSection, 'vertical', 1);
                      }}
                      style={{
                        ...styles.button,
                        width: '100%',
                        backgroundColor: '#ef4444',
                        '&:hover': {
                          backgroundColor: '#dc2626'
                        }
                      }}
                    >
                      {t('resetLayoutButton') || 'Réinitialiser la mise en page'}
                    </button>
                  </div>
                </div>
              );
            })()}

            {selectedSection && selectedSection === 'cancellation' && (
              <div style={styles.configSection}>
                <h3 style={styles.configTitle}>
                  <ScrollText size={20} /> {t('cancellationNotes') || 'Notes d\'annulation'}
                </h3>

                <div style={styles.configGroup}>
                  <label style={styles.switchLabel}>
                    <input
                      type="checkbox"
                      checked={config.cancellationNotes.enabled}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        cancellationNotes: {
                          ...prev.cancellationNotes,
                          enabled: e.target.checked
                        }
                      }))}
                      style={styles.checkbox}
                    />
                    <span style={styles.switchText}>
                      {t('enableCancellationNotes') || 'Activer les notes d\'annulation'}
                    </span>
                  </label>
                </div>

                {config.cancellationNotes.enabled && (
                  <>
                    {/* Politique personnalisée */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('customCancellationPolicy') || 'Politique d\'annulation personnalisée'}
                      </label>
                      <textarea
                        value={config.cancellationNotes.customPolicy}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          cancellationNotes: {
                            ...prev.cancellationNotes,
                            customPolicy: e.target.value
                          }
                        }))}
                        placeholder={t('enterCustomPolicy') || 'Entrez votre politique d\'annulation personnalisée...'}
                        style={styles.textarea}
                        rows={4}
                      />
                    </div>

                    {/* Liste des notes existantes */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('cancellationNotesList') || 'Notes d\'annulation'}
                      </label>

                      {config.cancellationNotes.notes.length > 0 ? (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          padding: '8px',
                          backgroundColor: '#374151',
                          borderRadius: '6px',
                          border: '1px solid #4a5568'
                        }}>
                          {config.cancellationNotes.notes.map((note, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: '#2d3748',
                                borderRadius: '4px',
                                border: '1px solid #4a5568'
                              }}
                            >
                              <span style={{
                                fontSize: '14px',
                                color: '#d1d5db',
                                flex: 1,
                                wordBreak: 'break-word'
                              }}>
                                {note}
                              </span>
                              <button
                                onClick={() => {
                                  const newNotes = [...config.cancellationNotes.notes];
                                  newNotes.splice(index, 1);
                                  setConfig(prev => ({
                                    ...prev,
                                    cancellationNotes: {
                                      ...prev.cancellationNotes,
                                      notes: newNotes
                                    }
                                  }));
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  marginLeft: '8px',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                title={t('removeNote') || 'Supprimer la note'}
                                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#374151',
                          borderRadius: '6px',
                          border: '1px dashed #4a5568',
                          textAlign: 'center',
                          color: '#9ca3af',
                          fontSize: '14px'
                        }}>
                          {t('noCancellationNotes') || 'Aucune note d\'annulation ajoutée'}
                        </div>
                      )}
                    </div>

                    {/* Ajouter une nouvelle note */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('addNewNote') || 'Ajouter une nouvelle note'}
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          id="newCancellationNote"
                          placeholder={t('enterNoteText') || 'Entrez le texte de la note...'}
                          style={{
                            ...styles.input,
                            flex: 1
                          }}
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('newCancellationNote');
                            const value = input.value.trim();

                            if (value) {
                              setConfig(prev => ({
                                ...prev,
                                cancellationNotes: {
                                  ...prev.cancellationNotes,
                                  notes: [...prev.cancellationNotes.notes.filter(note => note.trim() !== ''), value]
                                }
                              }));

                              input.value = '';
                            } else {
                              console.log('Input is empty');
                            }
                          }}
                          style={{
                            ...styles.button,
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          {t('add') || 'Ajouter'}
                        </button>
                      </div>
                    </div>

                    {/* Notes système prédéfinies */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('predefinedNotes') || 'Notes prédéfinies'}
                      </label>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginBottom: '10px'
                      }}>
                        {[
                          t('nonRefundableAfter24h') || 'Non-remboursable 24h après la réservation',
                          t('freeCancellationBefore48h') || 'Annulation gratuite jusqu\'à 48h avant',
                          t('partialRefund70percent') || 'Remboursement partiel de 70% avant 7 jours',
                          t('administrativeFees50') || 'Frais administratifs de 50€ pour toute annulation'
                        ].map((note, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setConfig(prev => ({
                                ...prev,
                                cancellationNotes: {
                                  ...prev.cancellationNotes,
                                  notes: [...prev.cancellationNotes.notes, note]
                                }
                              }));
                            }}
                            style={{
                              textAlign: 'left',
                              padding: '10px',
                              backgroundColor: '#374151',
                              border: '1px solid #4a5568',
                              borderRadius: '6px',
                              color: '#d1d5db',
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.borderColor = '#7c3aed';
                              e.target.style.backgroundColor = '#4a5568';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.borderColor = '#4a5568';
                              e.target.style.backgroundColor = '#374151';
                            }}
                          >
                            {note}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Option pour afficher les infos système */}
                    <div style={styles.configGroup}>
                      <label style={styles.switchLabel}>
                        <input
                          type="checkbox"
                          checked={config.cancellationNotes.showSystemInfo}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            cancellationNotes: {
                              ...prev.cancellationNotes,
                              showSystemInfo: e.target.checked
                            }
                          }))}
                          style={styles.checkbox}
                        />
                        <span style={styles.switchText}>
                          {t('showSystemCancellationInfo') || 'Afficher les informations système'}
                        </span>
                      </label>
                    </div>

                    {/* Bouton pour effacer toutes les notes */}
                    {config.cancellationNotes.notes.length > 0 && (
                      <div style={styles.configGroup}>
                        <button
                          onClick={() => {
                            if (window.confirm(t('confirmRemoveAllNotes') || 'Supprimer toutes les notes ?')) {
                              setConfig(prev => ({
                                ...prev,
                                cancellationNotes: {
                                  ...prev.cancellationNotes,
                                  notes: []
                                }
                              }));
                            }
                          }}
                          style={{
                            ...styles.button,
                            width: '100%',
                            backgroundColor: '#ef4444',
                            '&:hover': {
                              backgroundColor: '#dc2626'
                            }
                          }}
                        >
                          <Trash2 size={16} />
                          {t('removeAllNotes') || 'Supprimer toutes les notes'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {selectedSection && selectedSection === 'notes' && (
              <div style={styles.configSection}>
                <h3 style={styles.configTitle}>
                  <FileExclamationPoint size={20} /> {t('notesCustomization') || 'Personnalisation des Notes'}
                </h3>

                <div style={styles.configGroup}>
                  <label style={styles.switchLabel}>
                    <input
                      type="checkbox"
                      checked={config.notesConfig.showCustomizationPanel}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        notesConfig: {
                          ...prev.notesConfig,
                          showCustomizationPanel: e.target.checked
                        }
                      }))}
                      style={styles.checkbox}
                    />
                    <span style={styles.switchText}>
                      {t('showNotesCustomization') || 'Afficher la personnalisation des notes'}
                    </span>
                  </label>
                </div>

                {config.notesConfig.showCustomizationPanel && (
                  <>
                    {/* Liste des notes avec suppression */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('notesList') || 'Liste des notes'}
                      </label>

                      {config.notesConfig.notes && config.notesConfig.notes.length > 0 ? (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          padding: '8px',
                          backgroundColor: '#374151',
                          borderRadius: '6px',
                          border: '1px solid #4a5568'
                        }}>
                          {config.notesConfig.notes.filter(note => note.trim() !== '').map((note, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: '#2d3748',
                                borderRadius: '4px',
                                border: '1px solid #4a5568'
                              }}
                            >
                              <span style={{
                                fontSize: '14px',
                                color: '#d1d5db',
                                flex: 1,
                                wordBreak: 'break-word'
                              }}>
                                {note}
                              </span>
                              <button
                                onClick={() => {
                                  const newNotes = config.notesConfig.notes.filter((_, i) => i !== index);
                                  setConfig(prev => ({
                                    ...prev,
                                    notesConfig: {
                                      ...prev.notesConfig,
                                      notes: newNotes
                                    }
                                  }));
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                title={t('removeNote') || 'Supprimer la note'}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#374151',
                          borderRadius: '6px',
                          border: '1px dashed #4a5568',
                          textAlign: 'center',
                          color: '#9ca3af',
                          fontSize: '14px'
                        }}>
                          {t('noNotes') || 'Aucune note ajoutée'}
                        </div>
                      )}
                    </div>

                    {/* Ajouter une nouvelle note */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('addNewNote') || 'Ajouter une nouvelle note'}
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          id="newGeneralNote"
                          placeholder={t('enterNoteText') || 'Entrez le texte de la note...'}
                          style={{
                            ...styles.input,
                            flex: 1
                          }}
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('newGeneralNote');
                            const value = input.value.trim();

                            if (value) {
                              setConfig(prev => ({
                                ...prev,
                                notesConfig: {
                                  ...prev.notesConfig,
                                  notes: [...prev.notesConfig.notes, value]
                                }
                              }));
                              input.value = '';
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          {t('add') || 'Ajouter'}
                        </button>
                      </div>
                    </div>

                    {/* Contact d'urgence */}
                    <div style={styles.configGroup}>
                      <label style={styles.label}>
                        {t('emergencyContact') || 'Contact d\'urgence'}
                      </label>
                      <input
                        type="text"
                        value={config.notesConfig.emergencyContact}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          notesConfig: {
                            ...prev.notesConfig,
                            emergencyContact: e.target.value
                          }
                        }))}
                        placeholder={t('enterEmergencyContact') || 'Entrez un contact d\'urgence...'}
                        style={styles.input}
                      />
                    </div>

                    {/* Afficher la date de génération */}
                    <div style={styles.configGroup}>
                      <label style={styles.switchLabel}>
                        <input
                          type="checkbox"
                          checked={config.notesConfig.showGenerationDate}
                          onChange={(e) => setConfig(prev => ({
                            ...prev,
                            notesConfig: {
                              ...prev.notesConfig,
                              showGenerationDate: e.target.checked
                            }
                          }))}
                          style={styles.checkbox}
                        />
                        <span style={styles.switchText}>
                          {t('showGenerationDate') || 'Afficher la date de génération'}
                        </span>
                      </label>
                    </div>

                    {/* Bouton pour effacer toutes les notes */}
                    {config.notesConfig.notes.length > 0 && (
                      <div style={styles.configGroup}>
                        <button
                          onClick={() => {
                            if (window.confirm(t('confirmRemoveAllNotes') || 'Supprimer toutes les notes ?')) {
                              setConfig(prev => ({
                                ...prev,
                                notesConfig: {
                                  ...prev.notesConfig,
                                  notes: []
                                }
                              }));
                            }
                          }}
                          style={{
                            padding: '10px 16px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                        >
                          <Trash2 size={16} />
                          {t('removeAllNotes') || 'Supprimer toutes les notes'}
                        </button>
                      </div>
                    )}

                    {/* Bouton pour réinitialiser aux notes par défaut */}
                    <div style={styles.configGroup}>
                      <button
                        onClick={() => {
                          if (window.confirm(t('confirmResetToDefault') || 'Rétablir les notes par défaut ?')) {
                            setConfig(prev => ({
                              ...prev,
                              notesConfig: {
                                ...prev.notesConfig,
                                notes: [
                                  t('notePresentVoucher') || 'Présentez ce voucher à votre arrivée',
                                  t('noteKeepVoucher') || 'Conservez ce voucher avec vous',
                                  t('noteEmergencyContact') || 'Contactez-nous en cas d\'urgence',
                                  t('noteCancellationPolicies') || 'Consultez nos politiques d\'annulation'
                                ]
                              }
                            }));
                          }
                        }}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          width: '100%',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
                      >
                        {t('resetToDefaultNotes') || 'Rétablir les notes par défaut'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div >
      </div >

      {/* Styles CSS pour animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.5; }
            50% { opacity: 1; }
            100% { opacity: 0.5; }
          }
          
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: #1f2937;
          }
          
          ::-webkit-scrollbar-thumb {
            background: #4a5568;
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: #7c3aed;
          }
          
          * {
            box-sizing: border-box;
          }
          
          button:active {
            transform: translateY(1px);
          }
        `}
      </style >
    </div >
  );
};

export default VoucherCustomizer;